import { applyGlassBackfaceShader } from '@/infrastructure/engine3d/materials/glassBackfaceShader';
import { DescriptorTextureApplier, type TTextureSlotDescriptor } from '@/infrastructure/engine3d/textures/DescriptorTextureApplier';
import { EnvironmentController } from '@/infrastructure/engine3d/environment/EnvironmentController';
import { GroundShadowService } from '@/infrastructure/engine3d/scene/GroundShadowService';
import { ModelLoader } from '@/infrastructure/engine3d/assets/ModelLoader';
import { createMatcapGlassMaterial } from '@/infrastructure/engine3d/materials/matcapGlassMaterial';
import { AmbientLight, Box3, DoubleSide, Mesh, MeshBasicMaterial, Vector3, type BufferGeometry, type Material, type Object3D } from 'three';
import { RectAreaLightUniformsLib } from 'three/addons/lights/RectAreaLightUniformsLib.js';
import type { IProduct3DStrategy, TCameraFramingParams } from '@/infrastructure/engine3d/strategies/IProduct3DStrategy';
import type { TCameraFraming } from '@/application/ports/TCameraFraming';
import type { TCanvasSnapshot } from '@/application/ports/TCanvasSnapshot';
import type { TGlassMeshParams } from '@/application/ports/TProductMeshParams';
import type { TSide } from '@/domain/shared/TSide';
import type { TStrategyAttachContext } from '@/infrastructure/engine3d/strategies/TStrategyAttachContext';

type TGobeletStrategyParams = {
    getMaxAnisotropy: () => number;
    meshParams: TGlassMeshParams;
};

type TGobeletBoundingBox = { depth: number; height: number; width: number };

const GOBELET_ROOT_NAME = 'cup';
const GOBELET_INSIDE_NAME = 'cup_inside';
const GOBELET_CUSTOMIZABLE_NAME = 'cup_customizable';
const MODEL_SCALE = 100;
const CHILD_SCALE = 0.1;
const DEFAULT_MODEL_URL_TEMPLATE = '/models/SM_Gobelet_';

// Legacy Editor3DWorld spherical orbit values — keep the same viewing
// angle as the original hand-tuned camera setup.
const GOBELET_ORBIT_PHI = 1.1815;
const GOBELET_ORBIT_THETA = 3.11375;
// Padding on the fit radius so the cup body isn't glued to the frame.
const GOBELET_FIT_MARGIN = 1.1;

// The GLB has two meshes: `_1` = inner glass shell (matcap), `_2` =
// printable outer wall (backface shader). envMap comes from the
// EnvironmentController; both are re-initialised on WebGL context
// restore.
class GobeletStrategy implements IProduct3DStrategy {
    private readonly _customMaterial: MeshBasicMaterial;
    private readonly _envController = new EnvironmentController();
    private readonly _getMaxAnisotropy: () => number;
    private readonly _textureApplier = new DescriptorTextureApplier();
    private _ambient: AmbientLight | null = null;
    private _boundingBox: TGobeletBoundingBox | null = null;
    private _groundShadow: GroundShadowService | null = null;
    private _meshBaseName = 'SM_Gobelet_';
    private readonly _meshParams: TGlassMeshParams;
    private _root: Object3D | null = null;

    constructor(params: TGobeletStrategyParams) {
        this._customMaterial = new MeshBasicMaterial({
            depthTest: true,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -1,
            side: DoubleSide,
        });
        applyGlassBackfaceShader(this._customMaterial);
        this._getMaxAnisotropy = params.getMaxAnisotropy;
        this._meshParams = params.meshParams;
    }

    async attach(context: TStrategyAttachContext): Promise<void> {
        const { renderer, scene } = context;

        this._envController.initialize(scene, renderer);
        RectAreaLightUniformsLib.init();
        this._ambient = new AmbientLight('rgb(255, 255, 255)', 0.2);
        this._ambient.name = 'ambientLight';
        scene.add(this._ambient);

        const productTypeSuffix = this._meshParams.productType.match(/(\d+)/)?.[0] ?? '';
        this._meshBaseName = this._meshParams.meshBaseName ?? `SM_Gobelet_${productTypeSuffix}`;
        const modelUrl = this._meshParams.modelUrl ?? `${DEFAULT_MODEL_URL_TEMPLATE}${productTypeSuffix}.glb`;

        const model = await ModelLoader.loadModel(modelUrl);
        const root = model?.scene as Object3D | undefined;
        if (!root) {
            throw new Error(`GobeletStrategy: failed to load model at ${modelUrl}`);
        }
        root.name = GOBELET_ROOT_NAME;
        root.scale.set(MODEL_SCALE, MODEL_SCALE, MODEL_SCALE);
        this._configureMeshChildren(root);

        this._root = root;
        scene.add(root);
        // Don't blindly applyEnvmapToMesh(root) — the MatcapMaterial inner
        // glass throws on envMap assignment in refreshUniformsCommon.

        this._groundShadow = new GroundShadowService({
            getGobeletBoundingBox: () => this._boundingBox,
            scene,
        });
        this._groundShadow.createOrUpdate();

        // Locate the customizable outer wall (renamed to
        // GOBELET_CUSTOMIZABLE_NAME by _configureMeshChildren) — the
        // only face that receives the fabric-canvas texture.
        const printableMesh = _findPrintableMesh(root);
        if (!printableMesh) {
            throw new Error(`GobeletStrategy: no mesh named "${GOBELET_CUSTOMIZABLE_NAME}" in the loaded model`);
        }
        const descriptor: TTextureSlotDescriptor = {
            // Glass products are RECTO-only in the domain (see
            // EProductFormat) — Vue never emits a verso snapshot for a
            // cup. Tight-couple the descriptor to that reality rather
            // than defensively accepting a side that will never arrive.
            side: 'recto',
            mesh: printableMesh,
            materialIndex: 0,
            slotOptions: { anisotropy: this._getMaxAnisotropy(), flipY: false, mipmaps: true },
            onMaterialAssigned: (material: Material, snap: TCanvasSnapshot) => {
                // Transparent when the fabric bg is transparent so the
                // glass shows through; opaque otherwise.
                material.transparent = !snap.hasOpaqueBackground;
                (material as MeshBasicMaterial).depthWrite = snap.hasOpaqueBackground;
            },
        };
        this._textureApplier.configure([descriptor]);
    }

    applyCanvasTexture(side: TSide, snapshot: TCanvasSnapshot): void {
        this._textureApplier.apply(side, snapshot);
    }

    // Called by the engine after attach() has resolved — the root is in
    // the scene, so its world bounding box is stable. Ports the legacy
    // zoomOnSelection([root], 1.1, 0) math to a declarative framing:
    //   - target = world bbox center (respects wherever the mesh origin
    //     lands after MODEL_SCALE * CHILD_SCALE)
    //   - radius = fit-to-bbox * margin, spherical pinned to legacy
    //     phi/theta so the viewing angle is unchanged.
    getCameraFraming(params: TCameraFramingParams): TCameraFraming {
        if (!this._root) {
            throw new Error('GobeletStrategy.getCameraFraming called before attach');
        }
        const worldBox = new Box3().expandByObject(this._root);
        const target = new Vector3();
        worldBox.getCenter(target);
        const size = new Vector3();
        worldBox.getSize(size);
        const maxSize = Math.max(size.x, size.y, size.z);
        const fitHeightDistance = maxSize / (2 * Math.tan((Math.PI * params.fov) / 360));
        const fitWidthDistance = fitHeightDistance / params.aspect;
        const radius = Math.max(fitHeightDistance, fitWidthDistance) * GOBELET_FIT_MARGIN;
        // Narrow polar band keeps the cup interior hidden.
        return {
            maxDistance: 5,
            maxPolarAngle: Math.PI * 0.56,
            minDistance: 1.8,
            minPolarAngle: Math.PI * 0.37,
            position: { x: -0.75, y: target.y, z: 2.75 },
            spherical: { phi: GOBELET_ORBIT_PHI, radius, theta: GOBELET_ORBIT_THETA },
            target: { x: target.x, y: target.y, z: target.z },
        };
    }

    getRoot(): Object3D | undefined {
        return this._root ?? undefined;
    }

    detach(context: TStrategyAttachContext): void {
        const { scene } = context;
        if (this._root) {
            scene.remove(this._root);
            _disposeObjectTree(this._root);
            this._root = null;
        }
        if (this._ambient) {
            scene.remove(this._ambient);
            this._ambient.dispose();
            this._ambient = null;
        }
        this._textureApplier.dispose();
        this._customMaterial.dispose();
        this._groundShadow = null;
        this._boundingBox = null;
    }

    onContextRestored(context: TStrategyAttachContext): void {
        const { renderer, scene } = context;
        this._envController.initialize(scene, renderer);
    }

    private _configureMeshChildren(glbScene: Object3D): void {
        glbScene.traverse((child) => {
            if (!(child instanceof Mesh)) {
                return;
            }
            child.scale.set(CHILD_SCALE, CHILD_SCALE, CHILD_SCALE);
            child.castShadow = false;
            child.receiveShadow = false;

            if (child.name === `${this._meshBaseName}_1`) {
                // Only the inner glass geometry is the reference for the
                // GroundShadow bbox — sibling helper Object3Ds would
                // clobber. Camera target is derived from the WORLD bbox
                // of the whole root in getCameraFraming(), not this
                // local geometry read.
                this._boundingBox = _computeBoundingBoxSize(child.geometry, 10);

                child.geometry.computeVertexNormals();
                child.geometry.normalizeNormals();
                child.material = createMatcapGlassMaterial();
                child.name = GOBELET_INSIDE_NAME;
                return;
            }

            if (child.name === `${this._meshBaseName}_2`) {
                child.material = this._customMaterial;
                child.name = GOBELET_CUSTOMIZABLE_NAME;
                child.renderOrder = 1;
            }
        });
    }
}

function _findPrintableMesh(root: Object3D): Mesh | undefined {
    let found: Mesh | undefined;
    root.traverse((child) => {
        if (!found && child instanceof Mesh && child.name === GOBELET_CUSTOMIZABLE_NAME) {
            found = child;
        }
    });
    return found;
}

function _computeBoundingBoxSize(geometry: BufferGeometry, scaleFactor: number): TGobeletBoundingBox {
    const clone = geometry.clone();
    clone.computeBoundingBox();
    const box = clone.boundingBox;
    if (!box) {
        return { depth: 1, height: 1, width: 1 };
    }
    return {
        depth: (box.max.z - box.min.z) * scaleFactor,
        height: (box.max.y - box.min.y) * scaleFactor,
        width: (box.max.x - box.min.x) * scaleFactor,
    };
}

function _disposeObjectTree(root: Object3D): void {
    root.traverse((child) => {
        if (child instanceof Mesh) {
            child.geometry?.dispose();
            const { material } = child;
            if (Array.isArray(material)) {
                material.forEach((mat) => mat.dispose());
            } else {
                material?.dispose();
            }
        }
    });
}

export { GobeletStrategy };
